import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Plus, X, Lock } from "lucide-react";
import { api } from "@/lib/api";

const SectionLabel = ({ children, hint }) => (
  <div className="mb-3">
    <h4 className="text-xs uppercase tracking-[0.18em] text-[#1A1F2C] font-medium">{children}</h4>
    {hint && <p className="text-xs text-[#6B6358] mt-1">{hint}</p>}
  </div>
);

export default function ProgramRules({ brandId, program, canManage, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const seed = (p) => ({
    content_rules: {
      allowed_category_slugs: p.content_rules?.allowed_category_slugs || [],
      custom_categories: p.content_rules?.custom_categories || [],
      exclusions: p.content_rules?.exclusions || [],
      text_rules: p.content_rules?.text_rules || [],
    },
    asset_rules: {
      whitelisted_shape_ids: p.asset_rules?.whitelisted_shape_ids || [],
      whitelisted_image_ids: p.asset_rules?.whitelisted_image_ids || [],
    },
    production_rules: {
      instructions: p.production_rules?.instructions || [],
    },
  });
  const [form, setForm] = useState(seed(program));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const reqs = [api.get(`/brands/${brandId}/content-categories`)];
        if (canManage) {
          reqs.push(api.get(`/brands/${brandId}/brand-kit/shapes`));
          reqs.push(api.get(`/brands/${brandId}/brand-kit/library`));
        }
        const res = await Promise.all(reqs);
        if (!active) return;
        setCategories(res[0].data || []);
        if (canManage) {
          setShapes(res[1]?.data || []);
          setImages(res[2]?.data || []);
        }
      } catch (e) {
        if (active) setError(e.response?.data?.detail || "Could not load brand assets");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [brandId, canManage]);

  const cr = form.content_rules;
  const ar = form.asset_rules;
  const pr = form.production_rules;
  const setCR = (patch) => setForm((f) => ({ ...f, content_rules: { ...f.content_rules, ...patch } }));
  const setAR = (patch) => setForm((f) => ({ ...f, asset_rules: { ...f.asset_rules, ...patch } }));
  const setPR = (patch) => setForm((f) => ({ ...f, production_rules: { ...f.production_rules, ...patch } }));

  const toggle = (list, val) =>
    list.includes(val) ? list.filter((x) => x !== val) : [...list, val];

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const payload = {
        content_rules: {
          ...cr,
          custom_categories: cr.custom_categories.filter((x) => x.trim()),
          exclusions: cr.exclusions.filter((x) => x.item.trim()),
          text_rules: cr.text_rules.filter((x) => x.trim()),
        },
        asset_rules: ar,
        production_rules: { instructions: pr.instructions.filter((x) => x.trim()) },
      };
      const { data } = await api.patch(`/brands/${brandId}/programs/${program.program_id}/rules`, payload);
      onSaved?.(data);
      setForm(seed(data));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save rules");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid={`program-rules-${program.program_id}`} className="bg-[#1A1F2C]/[0.03] border border-[#1A1F2C]/10 rounded-lg p-4">
        <p className="text-sm text-[#6B6358] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading rules…</p>
      </div>
    );
  }

  const catLabel = (slug) => (categories.find((c) => c.slug === slug)?.name) || slug;

  // ----- Read-only marketer view -----
  if (!canManage) {
    const noContent =
      cr.allowed_category_slugs.length === 0 && cr.custom_categories.length === 0 &&
      cr.exclusions.length === 0 && cr.text_rules.length === 0;
    return (
      <div data-testid={`program-rules-${program.program_id}`} className="bg-[#1A1F2C]/[0.03] border border-[#1A1F2C]/10 rounded-lg p-5 space-y-5">
        <SectionLabel hint="The brand rules that apply when you build from this program.">Content rules</SectionLabel>
        {noContent ? (
          <p className="text-sm text-[#6B6358] italic">No content rules set for this program.</p>
        ) : (
          <div className="space-y-4">
            {(cr.allowed_category_slugs.length > 0 || cr.custom_categories.length > 0) && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-1.5">Allowed content</p>
                <div className="flex flex-wrap gap-2">
                  {cr.allowed_category_slugs.map((s) => (
                    <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#1F6B6B]/10 text-[#1F6B6B]">{catLabel(s)}</span>
                  ))}
                  {cr.custom_categories.map((s) => (
                    <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#1F6B6B]/10 text-[#1F6B6B]">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {cr.exclusions.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-1.5">Exclusions</p>
                <ul className="space-y-1">
                  {cr.exclusions.map((x, i) => (
                    <li key={i} className="text-sm text-[#3D3A35]">
                      <span className="font-medium">{x.item}</span>{x.reason ? <span className="text-[#6B6358]"> — {x.reason}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {cr.text_rules.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-1.5">Additional rules</p>
                <ul className="list-disc pl-5 space-y-1">
                  {cr.text_rules.map((x, i) => (<li key={i} className="text-sm text-[#3D3A35]">{x}</li>))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ----- Admin editor -----
  return (
    <div data-testid={`program-rules-${program.program_id}`} className="bg-white border border-[#1A1F2C]/15 rounded-lg p-5 space-y-7">
      {/* Content rules */}
      <section>
        <SectionLabel hint="Visible to marketers. Constrains the content they may place in this program.">Content rules</SectionLabel>

        <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-2">Allowed content categories</p>
        {categories.length === 0 ? (
          <p className="text-xs text-[#6B6358] italic mb-3">No content categories defined for this brand yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-3">
            {categories.map((c) => {
              const on = cr.allowed_category_slugs.includes(c.slug);
              return (
                <button
                  key={c.category_id}
                  type="button"
                  data-testid={`program-rules-cat-${c.slug}`}
                  onClick={() => setCR({ allowed_category_slugs: toggle(cr.allowed_category_slugs, c.slug) })}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? "border-[#1F6B6B] bg-[#1F6B6B] text-white" : "border-[#1A1F2C]/25 text-[#3D3A35] hover:border-[#1A1F2C]/50"}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
        <ChipAdder
          testid={`program-rules-${program.program_id}-customcat`}
          placeholder="Add a custom category…"
          values={cr.custom_categories}
          onChange={(v) => setCR({ custom_categories: v })}
        />

        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-2">Exclusions (specific items not allowed)</p>
          <div className="space-y-2">
            {cr.exclusions.map((row, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-start">
                <input
                  data-testid={`program-rules-exclusion-item-${i}`}
                  value={row.item}
                  onChange={(e) => setCR({ exclusions: cr.exclusions.map((r, j) => j === i ? { ...r, item: e.target.value } : r) })}
                  placeholder="Excluded item (e.g. ‘Competitor logos’)"
                  className="flex-1 min-w-[180px] px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm"
                />
                <input
                  data-testid={`program-rules-exclusion-reason-${i}`}
                  value={row.reason}
                  onChange={(e) => setCR({ exclusions: cr.exclusions.map((r, j) => j === i ? { ...r, reason: e.target.value } : r) })}
                  placeholder="Reason (optional)"
                  className="flex-1 min-w-[180px] px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm"
                />
                <button type="button" data-testid={`program-rules-exclusion-remove-${i}`} onClick={() => setCR({ exclusions: cr.exclusions.filter((_, j) => j !== i) })} className="p-2 text-[#B5462F]"><X size={16} /></button>
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid={`program-rules-${program.program_id}-add-exclusion`}
            onClick={() => setCR({ exclusions: [...cr.exclusions, { item: "", reason: "" }] })}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#1A1F2C] border border-[#1A1F2C]/25 rounded-full px-3 py-1.5 hover:bg-[#1A1F2C]/[0.04]"
          >
            <Plus size={13} /> Add exclusion
          </button>
        </div>

        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-2">Free-form content rules</p>
          <TextRuleList
            testid={`program-rules-${program.program_id}-text`}
            placeholder="e.g. Discounts must be less than 20%"
            values={cr.text_rules}
            onChange={(v) => setCR({ text_rules: v })}
          />
        </div>
      </section>

      {/* Asset rules */}
      <section className="pt-6 border-t border-[#1A1F2C]/10">
        <SectionLabel hint="Whitelist the brand assets this program may use. Leave empty to allow all.">Asset rules</SectionLabel>
        <AssetWhitelist
          label="Shapes & graphic devices"
          items={shapes}
          idKey="shape_id"
          selected={ar.whitelisted_shape_ids}
          onChange={(v) => setAR({ whitelisted_shape_ids: v })}
          testidPrefix="program-rules-shape"
        />
        <div className="mt-5">
          <AssetWhitelist
            label="Photography library"
            items={images}
            idKey="image_id"
            selected={ar.whitelisted_image_ids}
            onChange={(v) => setAR({ whitelisted_image_ids: v })}
            testidPrefix="program-rules-image"
          />
        </div>
      </section>

      {/* Production rules */}
      <section className="pt-6 border-t border-[#1A1F2C]/10">
        <SectionLabel hint={<span className="inline-flex items-center gap-1"><Lock size={11} /> Hidden from marketers. Instructions the AI uses for docmaker queries.</span>}>Production rules</SectionLabel>
        <TextRuleList
          testid={`program-rules-${program.program_id}-prod`}
          placeholder="e.g. Always render the disclaimer panel at 6pt minimum"
          values={pr.instructions}
          onChange={(v) => setPR({ instructions: v })}
        />
      </section>

      {error && <p className="text-sm text-[#B5462F]">{error}</p>}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          data-testid={`program-rules-${program.program_id}-save`}
          onClick={save}
          disabled={saving}
          className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save rules"}
        </button>
        {saved && <span className="text-xs text-[#1F6B6B] flex items-center gap-1"><CheckCircle2 size={14} /> Saved</span>}
      </div>
    </div>
  );
}

function ChipAdder({ values, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#1A1F2C]/[0.06] text-[#3D3A35]">
            {v}
            <button type="button" data-testid={`${testid}-remove-${v}`} onClick={() => onChange(values.filter((x) => x !== v))} className="text-[#B5462F]"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`${testid}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm"
        />
        <button type="button" data-testid={`${testid}-add`} onClick={add} className="px-3 py-2 text-xs text-[#1A1F2C] border border-[#1A1F2C]/25 rounded-md hover:bg-[#1A1F2C]/[0.04]">Add</button>
      </div>
    </div>
  );
}

function TextRuleList({ values, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2 items-start">
            <textarea
              data-testid={`${testid}-${i}`}
              value={v}
              onChange={(e) => onChange(values.map((x, j) => j === i ? e.target.value : x))}
              rows={1}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE] text-sm resize-y"
            />
            <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(values.filter((_, j) => j !== i))} className="p-2 text-[#B5462F]"><X size={16} /></button>
          </div>
        ))}
      </div>
      <button
        type="button"
        data-testid={`${testid}-add`}
        onClick={() => onChange([...values, ""])}
        className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#1A1F2C] border border-[#1A1F2C]/25 rounded-full px-3 py-1.5 hover:bg-[#1A1F2C]/[0.04]"
      >
        <Plus size={13} /> Add rule
      </button>
    </div>
  );
}

function AssetWhitelist({ label, items, idKey, selected, onChange, testidPrefix }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358] mb-2">{label}</p>
      {items.length === 0 ? (
        <p className="text-xs text-[#6B6358] italic">No {label.toLowerCase()} in this brand yet.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {items.map((it) => {
            const id = it[idKey];
            const on = selected.includes(id);
            const isPdf = it.image_url && /\.pdf(\?|#|$)/i.test(it.image_url);
            return (
              <button
                key={id}
                type="button"
                data-testid={`${testidPrefix}-${id}`}
                onClick={() => toggle(id)}
                className={`relative border rounded-lg overflow-hidden text-left transition-colors ${on ? "border-[#1F6B6B] ring-2 ring-[#1F6B6B]/30" : "border-[#1A1F2C]/15 hover:border-[#1A1F2C]/40"}`}
              >
                <div className="aspect-square bg-[#F8F5EE] flex items-center justify-center p-2">
                  {it.image_url && !isPdf ? (
                    <img src={it.image_url} alt={it.name || it.alt || ""} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">{isPdf ? "PDF" : "—"}</span>
                  )}
                </div>
                {on && <span className="absolute top-1 right-1 bg-[#1F6B6B] text-white rounded-full p-0.5"><CheckCircle2 size={12} /></span>}
                <p className="text-[10px] text-[#3D3A35] px-1.5 py-1 truncate">{it.name || it.alt || it.subject_tag || "Asset"}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
